import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Plus, Trash2, ArrowUpRight, ChevronUp, ChevronDown, Lock, Edit2 } from "lucide-react";
import { toast } from "sonner";

import { projectApi, formatApiError } from "@/services/projectApi";
import { useTier } from "@/contexts/AuthContext";
import { ShapePreview, BC_OPTIONS, BC_COLOR_OF, SEGMENTS_FOR } from "@/components/site/ShapePreview";
import { DxfImportModal } from "@/components/geometry/DxfImportModal";

/* ---------- atoms ---------- */

const SubSection = ({ index, title, lead, children, testid }) => (
    <section data-testid={testid} className="border border-[#d8dcdd] bg-[#ffffff] mb-6">
        <header className="flex items-baseline gap-5 px-6 py-5 border-b border-[#d8dcdd]">
            <span className="tc-section-index">{index}</span>
            <h3 className="text-[17px] font-medium tracking-tight text-[#0e1416]">{title}</h3>
            {lead && <span className="ml-auto tc-mono text-[10.5px] text-[#5a6669] hidden lg:inline">{lead}</span>}
        </header>
        <div className="p-6">{children}</div>
    </section>
);

const Lbl = ({ children, required }) => (
    <label className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">
        {children}
        {required && <span className="text-[#01696f]"> *</span>}
    </label>
);

const Input = ({ value, onChange, type = "text", placeholder, testid, disabled, step }) => (
    <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        disabled={disabled}
        className="w-full bg-transparent border-0 border-b border-[#d8dcdd] focus:border-[#01696f] disabled:opacity-50 outline-none px-0 py-1.5 text-[13.5px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors"
    />
);

const Select = ({ value, onChange, options, testid, disabled }) => (
    <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        disabled={disabled}
        className="w-full bg-transparent border border-[#d8dcdd] focus:border-[#01696f] disabled:opacity-50 outline-none px-3 py-2 text-[13px] text-[#0e1416] transition-colors"
    >
        {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ))}
    </select>
);

const Btn = ({ children, onClick, disabled, variant = "primary", testid, type = "button" }) => {
    const cls = variant === "primary"
        ? "bg-[#01696f] hover:bg-[#015a60] text-[#ffffff]"
        : variant === "ghost"
            ? "text-[#5a6669] hover:text-[#0e1416]"
            : "border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416]";
    return (
        <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={`inline-flex items-center gap-2 px-3.5 py-2 text-[12.5px] transition-colors disabled:opacity-60 ${cls}`}>
            {children}
        </button>
    );
};

const ErrorLine = ({ msg, testid }) => msg ? (
    <div data-testid={testid} className="tc-mono text-[11px] text-[#a13434] border-l-2 border-[#a13434] pl-3 py-1 my-3">{msg}</div>
) : null;

const Toolbar = ({ children }) => <div className="flex items-center justify-between mb-4">{children}</div>;

/* Phase 2C-ii.4 · Mix-row one-liner.
   Read-only mono caption summarising trial-mix-QC calibration evidence.
   Renders nothing when all relevant fields are null. */
const MixRowTmqcCaption = ({ mix }) => {
    const q = mix?.trial_mix_qc;
    if (!q) return null;

    const segments = [];
    // Strength: prefer 28 d, fall back to 56 d.
    if (q.fc_28d_MPa != null) segments.push(`28 d ${q.fc_28d_MPa} MPa`);
    else if (q.fc_56d_MPa != null) segments.push(`56 d ${q.fc_56d_MPa} MPa`);
    // Consistency: prefer slump, fall back to slump flow, then flow table.
    if (q.slump_mm != null) segments.push(`slump ${q.slump_mm}`);
    else if (q.slump_flow_mm != null) segments.push(`slump flow ${q.slump_flow_mm}`);
    else if (q.flow_table_mm != null) segments.push(`flow table ${q.flow_table_mm}`);
    // W/CM verified — omit segment entirely if null.
    if (q.w_cm_verified != null) segments.push(`w/cm ${q.w_cm_verified}`);

    if (segments.length === 0) return null;

    return (
        <div
            data-testid={`mix-row-tmqc-caption-${mix.id}`}
            className="mt-2 pt-2 border-t border-dashed border-[#d8dcdd] tc-mono text-[10.5px] text-[#8a9498] tracking-wide"
        >
            tmqc · {segments.join(" · ")}
        </div>
    );
};

/* ---------- main ---------- */

export default function SetupTab() {
    const { project } = useOutletContext();
    const { tier, meets } = useTier();
    const pid = project.id;

    const [mixes, setMixes] = useState([]);
    const [geoms, setGeoms] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [pours, setPours] = useState([]);
    const [aggLib, setAggLib] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        try {
            const [m, g, s, p, a] = await Promise.all([
                projectApi.listMixes(pid),
                projectApi.listGeometries(pid),
                projectApi.listScenarios(pid),
                projectApi.listPours(pid),
                projectApi.listAggregates(),
            ]);
            setMixes(m); setGeoms(g); setScenarios(s); setPours(p); setAggLib(a);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pid]);

    if (loading) return <div className="tc-mono text-[11px] text-[#5a6669]">Loading setup…</div>;

    return (
        <div data-testid="setup-tab" className="flex flex-col">
            <SubSectionA project={project} pours={pours} mixes={mixes} geoms={geoms} scenarios={scenarios} onReload={reload} />
            <SubSectionB pid={pid} mixes={mixes} aggLib={aggLib} tier={tier} meets={meets} onReload={reload} />
            <SubSectionC pid={pid} geoms={geoms} tier={tier} meets={meets} onReload={reload} />
            <SubSectionD pid={pid} scenarios={scenarios} onReload={reload} />

            {/* CTA → Maturity tab */}
            <div className="border border-[#d8dcdd] bg-[#eef0ef] px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-[13.5px] text-[#0e1416] leading-[1.55] max-w-[68ch]">
                    Each mix needs a maturity method and a strength calibration before predictions become meaningful.
                    Configure them in <strong>Maturity &amp; Strength</strong>.
                </p>
                <Link to={`/app/projects/${pid}/maturity`} data-testid="setup-go-maturity" className="inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-3.5 py-2 text-[12.5px] transition-colors">
                    Maturity &amp; Strength <ArrowUpRight size={13} strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    );
}

/* ========== A. Project + Pour sequence ========== */

const SubSectionA = ({ project, pours, mixes, geoms, scenarios, onReload }) => {
    const [adding, setAdding] = useState(false);
    const [editingProject, setEditingProject] = useState(false);
    const [projForm, setProjForm] = useState({ name: project.name, location: project.location, status: project.status });

    const saveProject = async () => {
        try {
            await projectApi.updateProject(project.id, projForm);
            toast.success("Project updated.");
            setEditingProject(false);
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <SubSection index="A" title="Project & pour sequence" lead={`${pours.length} pours`} testid="setup-section-a">
            {/* Project summary */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                {!editingProject && (
                    <>
                        <SummaryCell label="Name" value={project.name} />
                        <SummaryCell label="Type" value={project.project_type} mono />
                        <SummaryCell label="Location" value={project.location} />
                        <SummaryCell label="Status" value={project.status} mono />
                    </>
                )}
                {editingProject && (
                    <>
                        <EditCell label="Name"><Input value={projForm.name} onChange={(e) => setProjForm({ ...projForm, name: e.target.value })} testid="setup-edit-project-name" /></EditCell>
                        <EditCell label="Location"><Input value={projForm.location} onChange={(e) => setProjForm({ ...projForm, location: e.target.value })} /></EditCell>
                        <EditCell label="Status">
                            <Select value={projForm.status} onChange={(e) => setProjForm({ ...projForm, status: e.target.value })} options={[
                                { value: "draft", label: "draft" }, { value: "active", label: "active" }, { value: "archived", label: "archived" },
                            ]} />
                        </EditCell>
                    </>
                )}
                <div className="sm:col-span-1 flex items-end justify-end gap-2">
                    {editingProject ? (
                        <>
                            <Btn variant="ghost" onClick={() => setEditingProject(false)}>Cancel</Btn>
                            <Btn onClick={saveProject} testid="setup-save-project">Save</Btn>
                        </>
                    ) : (
                        <Btn variant="secondary" onClick={() => setEditingProject(true)} testid="setup-edit-project">Edit project</Btn>
                    )}
                </div>
            </div>

            {/* Pour list */}
            <div className="border-t border-[#d8dcdd] pt-5">
                <Toolbar>
                    <span className="tc-eyebrow text-[#5a6669]">Pour sequence</span>
                    <Btn onClick={() => setAdding(true)} testid="setup-add-pour"><Plus size={13} strokeWidth={1.5} />Add pour</Btn>
                </Toolbar>

                {pours.length === 0 && !adding && (
                    <p className="text-[13px] text-[#5a6669] leading-[1.6] max-w-[68ch] mt-4">
                        No pours yet. A pour links one mix, one geometry, and one thermal scenario.
                        Most mass-concrete projects sequence multiple pours — calibration improves as the sequence progresses.
                    </p>
                )}

                {pours.length > 0 && (
                    <ul className="divide-y divide-[#d8dcdd] border-t border-b border-[#d8dcdd]">
                        {pours.map((p, idx) => (
                            <PourRow key={p.id} p={p} idx={idx} total={pours.length} mixes={mixes} geoms={geoms} scenarios={scenarios} onReload={onReload} pid={project.id} />
                        ))}
                    </ul>
                )}

                {adding && (
                    <PourForm pid={project.id} mixes={mixes} geoms={geoms} scenarios={scenarios} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />
                )}
            </div>
        </SubSection>
    );
};

const SummaryCell = ({ label, value, mono }) => (
    <div className="flex flex-col gap-1">
        <Lbl>{label}</Lbl>
        <span className={`text-[13.5px] text-[#0e1416] ${mono ? "tc-mono text-[12.5px]" : ""}`}>{value}</span>
    </div>
);
const EditCell = ({ label, children }) => (<div className="flex flex-col gap-1.5"><Lbl>{label}</Lbl>{children}</div>);

const PourRow = ({ p, idx, total, mixes, geoms, scenarios, onReload, pid }) => {
    const [editing, setEditing] = useState(false);
    const mix = mixes.find((m) => m.id === p.mix_id);
    const geom = geoms.find((g) => g.id === p.geometry_id);
    const sc = scenarios.find((s) => s.id === p.scenario_id);
    const onMove = async (dir) => {
        const list = []; // build swap
        // fetch fresh from caller via onReload — for simplicity, swap with neighbour by sequence
        try {
            const fresh = await projectApi.listPours(pid);
            const sorted = [...fresh].sort((a, b) => a.sequence_index - b.sequence_index);
            const i = sorted.findIndex((x) => x.id === p.id);
            const j = i + dir;
            if (j < 0 || j >= sorted.length) return;
            const a = sorted[i]; const b = sorted[j];
            await projectApi.reorderPours(pid, [
                { id: a.id, sequence_index: b.sequence_index },
                { id: b.id, sequence_index: a.sequence_index },
            ]);
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const onDelete = async () => {
        try { await projectApi.deletePour(p.id); onReload(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <li data-testid={`pour-row-${p.id}`} className="px-1 py-3 grid grid-cols-12 items-center gap-2">
            <div className="col-span-1 tc-mono text-[12px] text-[#5a6669]">#{p.sequence_index}</div>
            <div className="col-span-3">
                <div className="text-[13.5px] text-[#0e1416]">{p.name}</div>
                <div className="tc-mono text-[10.5px] text-[#5a6669]">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString() : "no date"}{p.results_stale ? " · stale" : ""}</div>
            </div>
            <div className="col-span-3 tc-mono text-[11px] text-[#5a6669]">{mix ? `mix · ${mix.name}` : "no mix"}</div>
            <div className="col-span-2 tc-mono text-[11px] text-[#5a6669]">{geom ? `geom · ${geom.shape_type}` : "no geom"}</div>
            <div className="col-span-2 tc-mono text-[11px] text-[#5a6669]">{sc ? `sc · ${sc.name}` : "no scenario"}</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
                <button onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 text-[#5a6669] hover:text-[#0e1416] disabled:opacity-30" title="Move up"><ChevronUp size={13} strokeWidth={1.5} /></button>
                <button onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 text-[#5a6669] hover:text-[#0e1416] disabled:opacity-30" title="Move down"><ChevronDown size={13} strokeWidth={1.5} /></button>
                <button onClick={() => setEditing(true)} className="p-1 text-[#5a6669] hover:text-[#01696f]" title="Edit pour" data-testid={`pour-edit-${p.id}`}><Edit2 size={13} strokeWidth={1.5} /></button>
                <button onClick={onDelete} className="p-1 text-[#5a6669] hover:text-[#a13434]" title="Delete"><Trash2 size={13} strokeWidth={1.5} /></button>
            </div>
            {editing && (
                <div className="col-span-12 mt-3">
                    <PourForm pid={pid} mixes={mixes} geoms={geoms} scenarios={scenarios}
                              editing={p}
                              onCancel={() => setEditing(false)}
                              onSaved={() => { setEditing(false); onReload(); }} />
                </div>
            )}
        </li>
    );
};

const PourForm = ({ pid, mixes, geoms, scenarios, editing, onCancel, onSaved }) => {
    const [f, setF] = useState(() => editing ? {
        name: editing.name || "",
        scheduled_at: editing.scheduled_at ? new Date(editing.scheduled_at).toISOString().slice(0, 16) : "",
        mix_id: editing.mix_id || mixes[0]?.id || "",
        geometry_id: editing.geometry_id || geoms[0]?.id || "",
        scenario_id: editing.scenario_id || scenarios[0]?.id || "",
    } : { name: "", scheduled_at: "", mix_id: mixes[0]?.id || "", geometry_id: geoms[0]?.id || "", scenario_id: scenarios[0]?.id || "" });
    const [err, setErr] = useState(null);
    const save = async () => {
        setErr(null);
        if (!f.name.trim()) return setErr("Pour name is required.");
        try {
            const body = {
                name: f.name, sequence_index: editing?.sequence_index ?? 0,
                mix_id: f.mix_id || null, geometry_id: f.geometry_id || null, scenario_id: f.scenario_id || null,
                scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
            };
            if (editing) await projectApi.updatePour(editing.id, body);
            else await projectApi.createPour(pid, body);
            toast.success(editing ? "Pour updated." : "Pour added.");
            onSaved();
        } catch (e) { setErr(formatApiError(e)); }
    };
    return (
        <div data-testid="pour-form" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5 mt-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <EditCell label="Name"><Input testid="pour-form-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Pour 1 — base slab" /></EditCell>
                <EditCell label="Scheduled"><Input type="date" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} /></EditCell>
                <EditCell label="Mix"><Select value={f.mix_id} onChange={(e) => setF({ ...f, mix_id: e.target.value })} options={[{ value: "", label: "— none —" }, ...mixes.map((m) => ({ value: m.id, label: m.name }))]} /></EditCell>
                <EditCell label="Geometry"><Select value={f.geometry_id} onChange={(e) => setF({ ...f, geometry_id: e.target.value })} options={[{ value: "", label: "— none —" }, ...geoms.map((g) => ({ value: g.id, label: `${g.name} · ${g.shape_type}` }))]} /></EditCell>
                <EditCell label="Scenario"><Select value={f.scenario_id} onChange={(e) => setF({ ...f, scenario_id: e.target.value })} options={[{ value: "", label: "— none —" }, ...scenarios.map((s) => ({ value: s.id, label: s.name }))]} /></EditCell>
            </div>
            <ErrorLine msg={err} testid="pour-form-error" />
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[#d8dcdd]">
                <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} testid="pour-form-save">Save pour</Btn>
            </div>
        </div>
    );
};

/* ========== B. Mix definition ========== */

const SubSectionB = ({ pid, mixes, aggLib, tier, meets, onReload }) => {
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const onDelete = async (id) => {
        try { await projectApi.deleteMix(id); onReload(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <SubSection index="B" title="Mix definition" lead={`${mixes.length} mix${mixes.length === 1 ? "" : "es"}`} testid="setup-section-b">
            <Toolbar>
                <span className="tc-eyebrow text-[#5a6669]">Project mixes</span>
                <Btn onClick={() => { setEditingId(null); setAdding(true); }} testid="setup-add-mix"><Plus size={13} strokeWidth={1.5} />Add mix</Btn>
            </Toolbar>
            {mixes.length === 0 && !adding && (
                <p className="text-[13px] text-[#5a6669] mt-4">No mixes yet. Define at least one mix with binder, w/b, and aggregate; then configure its maturity method.</p>
            )}
            {mixes.length > 0 && (
                <ul className="divide-y divide-[#d8dcdd] border-t border-b border-[#d8dcdd]">
                    {mixes.map((m) => (
                        <li key={m.id} data-testid={`mix-row-${m.id}`} className="px-1 py-3">
                            <div className="grid grid-cols-12 items-center">
                                <div className="col-span-4 text-[13.5px] text-[#0e1416]">{m.name}</div>
                                <div className="col-span-3 tc-mono text-[11px] text-[#5a6669]">{m.binder_type}{m.binder_type !== "OPC" ? ` · ${[m.pfa_pct ? `PFA ${m.pfa_pct}%` : null, m.ggbs_pct ? `GGBS ${m.ggbs_pct}%` : null].filter(Boolean).join(" · ")}` : ""}</div>
                                <div className="col-span-2 tc-mono text-[11px] text-[#5a6669]">w/b = {m.w_b_ratio.toFixed(2)}</div>
                                <div className="col-span-2 tc-mono text-[11px] text-[#5a6669]">{m.aggregate_key}</div>
                                <div className="col-span-1 flex justify-end gap-1">
                                    <Link to={`/app/projects/${pid}/maturity?mix=${m.id}`} className="p-1 text-[#01696f] hover:text-[#015a60]" title="Maturity & Strength"><ArrowUpRight size={13} strokeWidth={1.5} /></Link>
                                    <button onClick={() => { setAdding(false); setEditingId(m.id); }} className="p-1 text-[#5a6669] hover:text-[#01696f]" title="Edit" data-testid={`mix-edit-${m.id}`}><Edit2 size={13} strokeWidth={1.5} /></button>
                                    <button onClick={() => onDelete(m.id)} className="p-1 text-[#5a6669] hover:text-[#a13434]" title="Delete"><Trash2 size={13} strokeWidth={1.5} /></button>
                                </div>
                            </div>
                            <MixRowTmqcCaption mix={m} />
                        </li>
                    ))}
                </ul>
            )}
            {adding && (
                <MixForm pid={pid} aggLib={aggLib} tier={tier} meets={meets} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />
            )}
            {editingId && (
                <MixForm pid={pid} aggLib={aggLib} tier={tier} meets={meets} editing={mixes.find((m) => m.id === editingId)}
                         onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); onReload(); }} />
            )}
        </SubSection>
    );
};

/* Trial-Mix-QC field bounds (mirror backend Pydantic validators in /app/backend/server.py · TrialMixQc). */
const TMQC_BOUNDS = {
    slump_mm:        { min: 0,    max: 250,  label: "Slump",        unit: "mm", msg: "Slump must be 0–250 mm." },
    slump_flow_mm:   { min: 450,  max: 850,  label: "Slump flow",   unit: "mm", msg: "Slump flow must be 450–850 mm." },
    flow_table_mm:   { min: 340,  max: 620,  label: "Flow table",   unit: "mm", msg: "Flow table must be 340–620 mm." },
    air_content_pct: { min: 0,    max: 15,   label: "Air content",  unit: "%",  msg: "Air content must be 0–15 %." },
    fc_7d_MPa:       { min: 0,    max: 200,  label: "fc 7 d",       unit: "MPa", msg: "fc 7 d must be 0–200 MPa." },
    fc_14d_MPa:      { min: 0,    max: 200,  label: "fc 14 d",      unit: "MPa", msg: "fc 14 d must be 0–200 MPa." },
    fc_28d_MPa:      { min: 0,    max: 200,  label: "fc 28 d",      unit: "MPa", msg: "fc 28 d must be 0–200 MPa." },
    fc_56d_MPa:      { min: 0,    max: 200,  label: "fc 56 d",      unit: "MPa", msg: "fc 56 d must be 0–200 MPa." },
    w_cm_verified:   { min: 0,    max: 1,    label: "W/CM verified", unit: "",   msg: "W/CM verified must be 0–1." },
};

const EMPTY_TMQC = {
    slump_mm: "", slump_flow_mm: "", flow_table_mm: "",
    air_content_pct: "",
    fc_7d_MPa: "", fc_14d_MPa: "", fc_28d_MPa: "", fc_56d_MPa: "",
    w_cm_verified: "",
    sampling_lab: "", sampling_date: "", notes: "",
};

const tmqcFromDoc = (q) => {
    if (!q) return { ...EMPTY_TMQC };
    const out = { ...EMPTY_TMQC };
    Object.keys(EMPTY_TMQC).forEach((k) => {
        out[k] = q[k] === null || q[k] === undefined ? "" : q[k];
    });
    return out;
};

const validateTmqcField = (key, raw) => {
    if (raw === "" || raw === null || raw === undefined) return null;
    const b = TMQC_BOUNDS[key];
    if (!b) return null;
    const v = Number(raw);
    if (Number.isNaN(v)) return `${b.label} must be a number.`;
    if (v < b.min || v > b.max) return b.msg;
    return null;
};

const tmqcHasAny = (q) =>
    Object.entries(q).some(([k, v]) => v !== "" && v !== null && v !== undefined && !(k === "sampling_lab" || k === "sampling_date" || k === "notes" ? false : false));

const MixForm = ({ pid, aggLib, tier, meets, editing, onCancel, onSaved }) => {
    const defaultAgg = aggLib[3] || aggLib[0] || { key: "generic", cte: 10e-6 };
    const initial = useMemo(() => editing ? {
        name: editing.name || "",
        binder_type: editing.binder_type || "OPC",
        pfa_pct: editing.pfa_pct || 0,
        ggbs_pct: editing.ggbs_pct || 0,
        w_b_ratio: editing.w_b_ratio ?? 0.40,
        aggregate_key: editing.aggregate_key || defaultAgg.key,
        aggregate_cte: editing.aggregate_cte ?? defaultAgg.cte,
        cementitious_kg_m3: editing.cementitious_kg_m3 ?? 380,
        notes: editing.notes || "",
    } : {
        name: "", binder_type: "OPC", pfa_pct: 0, ggbs_pct: 0, w_b_ratio: 0.40,
        aggregate_key: defaultAgg.key, aggregate_cte: defaultAgg.cte, cementitious_kg_m3: 380, notes: "",
    }, [editing, defaultAgg.key, defaultAgg.cte]);

    const [f, setF] = useState(initial);
    const [tmqc, setTmqc] = useState(() => tmqcFromDoc(editing?.trial_mix_qc));
    const [tmqcOpen, setTmqcOpen] = useState(() => Boolean(editing?.trial_mix_qc));
    const [err, setErr] = useState(null);
    const canBlend = meets("starter");
    const isBlend = f.binder_type !== "OPC";

    const setAgg = (key) => {
        const agg = aggLib.find((a) => a.key === key) || defaultAgg;
        setF((s) => ({ ...s, aggregate_key: key, aggregate_cte: agg.cte }));
    };

    const fieldErrors = useMemo(() => {
        const errs = {};
        Object.keys(TMQC_BOUNDS).forEach((k) => {
            const e = validateTmqcField(k, tmqc[k]);
            if (e) errs[k] = e;
        });
        return errs;
    }, [tmqc]);
    const hasTmqcErrors = Object.keys(fieldErrors).length > 0;

    const buildTmqcPayload = () => {
        const out = {};
        Object.keys(EMPTY_TMQC).forEach((k) => {
            const raw = tmqc[k];
            if (raw === "" || raw === null || raw === undefined) return;
            if (k === "sampling_lab" || k === "sampling_date" || k === "notes") {
                const s = String(raw).trim();
                if (s) out[k] = s;
            } else {
                const v = Number(raw);
                if (!Number.isNaN(v)) out[k] = v;
            }
        });
        return Object.keys(out).length ? out : null;
    };

    const save = async () => {
        setErr(null);
        if (!f.name.trim()) return setErr("Mix name is required.");
        if (hasTmqcErrors) return setErr("Resolve trial-mix QC field errors before saving.");
        try {
            const body = { ...f, trial_mix_qc: buildTmqcPayload() };
            if (editing) {
                await projectApi.updateMix(editing.id, body);
                toast.success("Mix updated.");
            } else {
                await projectApi.createMix(pid, body);
                toast.success("Mix saved.");
            }
            onSaved();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div data-testid="mix-form" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5 mt-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <EditCell label="Name"><Input testid="mix-form-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="M-32 / PFA-25" /></EditCell>
                <EditCell label={`Binder${!canBlend ? " (Demo: OPC only)" : ""}`}>
                    <Select value={f.binder_type} onChange={(e) => setF({ ...f, binder_type: e.target.value })} testid="mix-form-binder" options={[
                        { value: "OPC", label: "OPC" },
                        { value: "OPC+PFA", label: canBlend ? "OPC + PFA" : "OPC + PFA · Starter+" },
                        { value: "OPC+GGBS", label: canBlend ? "OPC + GGBS" : "OPC + GGBS · Starter+" },
                        { value: "OPC+PFA+GGBS", label: canBlend ? "OPC + PFA + GGBS" : "Ternary · Starter+" },
                    ]} disabled={false} />
                </EditCell>
                <EditCell label="W / B ratio"><Input type="number" step="0.01" value={f.w_b_ratio} onChange={(e) => setF({ ...f, w_b_ratio: parseFloat(e.target.value) || 0 })} /></EditCell>
                {isBlend && (
                    <>
                        <EditCell label="PFA %"><Input type="number" step="1" value={f.pfa_pct} onChange={(e) => setF({ ...f, pfa_pct: parseFloat(e.target.value) || 0 })} disabled={!canBlend || f.binder_type === "OPC+GGBS"} /></EditCell>
                        <EditCell label="GGBS %"><Input type="number" step="1" value={f.ggbs_pct} onChange={(e) => setF({ ...f, ggbs_pct: parseFloat(e.target.value) || 0 })} disabled={!canBlend || f.binder_type === "OPC+PFA"} /></EditCell>
                        <EditCell label="OPC % (derived)"><Input value={100 - (f.pfa_pct || 0) - (f.ggbs_pct || 0)} disabled /></EditCell>
                    </>
                )}
                <EditCell label="Aggregate">
                    <Select value={f.aggregate_key} onChange={(e) => setAgg(e.target.value)} options={aggLib.map((a) => ({ value: a.key, label: `${a.label} · α = ${(a.cte * 1e6).toFixed(1)} × 10⁻⁶ /°C` }))} />
                </EditCell>
                <EditCell label="Aggregate CTE (/°C)"><Input type="number" step="0.0000005" value={f.aggregate_cte} onChange={(e) => setF({ ...f, aggregate_cte: parseFloat(e.target.value) || 0 })} /></EditCell>
                <EditCell label="Cementitious (kg/m³)"><Input type="number" step="5" value={f.cementitious_kg_m3} onChange={(e) => setF({ ...f, cementitious_kg_m3: parseFloat(e.target.value) || 0 })} /></EditCell>
                <div className="sm:col-span-3"><EditCell label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Project context, supplier, lab data ref…" /></EditCell></div>
            </div>
            {!canBlend && isBlend && (
                <p className="mt-3 tc-mono text-[10.5px] text-[#5a6669]"><Lock size={11} strokeWidth={1.5} className="inline-block mr-1" />Blended binders available on Starter and above.</p>
            )}

            {/* Trial-Mix-QC (optional, collapsible) */}
            <TrialMixQcSection
                open={tmqcOpen} onToggle={() => setTmqcOpen((v) => !v)}
                tmqc={tmqc} setTmqc={setTmqc} fieldErrors={fieldErrors}
            />

            <ErrorLine msg={err} testid="mix-form-error" />
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[#d8dcdd]">
                <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} testid="mix-form-save" disabled={hasTmqcErrors}>{editing ? "Save changes" : "Save mix"}</Btn>
            </div>
        </div>
    );
};

/* Trial-Mix-QC sub-form — collapsible, all fields optional.
   Renders inside MixForm under the binder/aggregate block. */
const TmqcField = ({ field, tmqc, setTmqc, error, type = "number", step, placeholder }) => {
    const b = TMQC_BOUNDS[field];
    const labelText = b ? `${b.label}${b.unit ? ` (${b.unit})` : ""}` : field;
    return (
        <div className="flex flex-col gap-1.5">
            <Lbl>{labelText}</Lbl>
            <Input
                type={type}
                step={step}
                value={tmqc[field]}
                onChange={(e) => setTmqc({ ...tmqc, [field]: e.target.value })}
                placeholder={placeholder}
                testid={`mix-form-tmqc-${field}`}
            />
            {error && (
                <span data-testid={`mix-form-tmqc-${field}-error`} className="tc-mono text-[10.5px] text-[#a13434]">{error}</span>
            )}
        </div>
    );
};

const TrialMixQcSection = ({ open, onToggle, tmqc, setTmqc, fieldErrors }) => (
    <div className="mt-5 pt-4 border-t border-[#d8dcdd]" data-testid="mix-form-tmqc">
        <button
            type="button"
            onClick={onToggle}
            data-testid="mix-form-tmqc-toggle"
            className="flex items-center gap-2 tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669] hover:text-[#01696f] transition-colors"
        >
            {open ? <ChevronDown size={13} strokeWidth={1.5} /> : <ChevronUp size={13} strokeWidth={1.5} className="rotate-180" />}
            Trial mix QC (optional)
        </button>
        {open && (
            <div className="mt-4" data-testid="mix-form-tmqc-body">
                <p className="tc-mono text-[10.5px] text-[#5a6669] mb-3 leading-[1.55] max-w-[78ch]">
                    Lab sample data for this mix. All fields optional — record only what was measured. Used by Report 2 and field-calibration diagnostics; absence renders gracefully.
                </p>

                <div className="mt-3">
                    <span className="tc-mono text-[10px] tracking-widest uppercase text-[#01696f]">Fresh consistency (use whichever method applies)</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                        <TmqcField field="slump_mm"      tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.slump_mm}      step="1"  placeholder="0–250" />
                        <TmqcField field="slump_flow_mm" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.slump_flow_mm} step="5"  placeholder="450–850" />
                        <TmqcField field="flow_table_mm" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.flow_table_mm} step="5"  placeholder="340–620" />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <TmqcField field="air_content_pct" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.air_content_pct} step="0.1" placeholder="0–15" />
                    <TmqcField field="w_cm_verified"   tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.w_cm_verified}   step="0.01" placeholder="0–1" />
                    <div />
                    <TmqcField field="fc_7d_MPa"  tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.fc_7d_MPa}  step="0.1" />
                    <TmqcField field="fc_14d_MPa" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.fc_14d_MPa} step="0.1" />
                    <TmqcField field="fc_28d_MPa" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.fc_28d_MPa} step="0.1" />
                    <TmqcField field="fc_56d_MPa" tmqc={tmqc} setTmqc={setTmqc} error={fieldErrors.fc_56d_MPa} step="0.1" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <div className="flex flex-col gap-1.5">
                        <Lbl>Sampling lab</Lbl>
                        <Input value={tmqc.sampling_lab} onChange={(e) => setTmqc({ ...tmqc, sampling_lab: e.target.value })}
                               testid="mix-form-tmqc-sampling_lab" placeholder="e.g. HKCL · Lab 4" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Lbl>Sampling date</Lbl>
                        <Input type="date" value={tmqc.sampling_date}
                               onChange={(e) => setTmqc({ ...tmqc, sampling_date: e.target.value })}
                               testid="mix-form-tmqc-sampling_date" />
                    </div>
                    <div className="sm:col-span-3 flex flex-col gap-1.5">
                        <Lbl>Notes</Lbl>
                        <Input value={tmqc.notes} onChange={(e) => setTmqc({ ...tmqc, notes: e.target.value })}
                               testid="mix-form-tmqc-notes" placeholder="Cylinder/cube break notes, deviations, lab ref id…" />
                    </div>
                </div>
            </div>
        )}
    </div>
);

/* ========== C. Geometry ========== */

const SHAPE_DEFS = {
    mass_pour: { label: "Mass pour (rectangular)", defaults: { width_m: 2.8, height_m: 2.0, formwork_R: 0 } },
    phc_pile: { label: "PHC pile", defaults: { outer_diameter_mm: 600, wall_thickness_mm: 100, inner_void_mm: 400 } },
    slab: { label: "Slab", defaults: { width_m: 4.0, thickness_m: 0.4, supported: false } },
    wall: { label: "Wall", defaults: { thickness_m: 0.4, height_m: 3.0, exposure: "two-sided" } },
};

const SubSectionC = ({ pid, geoms, tier, meets, onReload }) => {
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [dxfFor, setDxfFor] = useState(null);   // geometry doc to import into
    const isPro = meets("professional");
    const onDelete = async (id) => {
        try { await projectApi.deleteGeometry(id); onReload(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const startDxfImport = async () => {
        // DXF import commits into an existing geometry row. If the project has
        // none yet, create a placeholder mass_pour first so commit has somewhere
        // to land.
        try {
            let target = geoms[0];
            if (!target) {
                target = await projectApi.createGeometry(pid, {
                    name: "DXF cross-section",
                    shape_type: "mass_pour",
                    parameters: { width_m: 1, height_m: 1 },
                    boundary_conditions: [],
                });
                onReload();
            }
            setDxfFor(target);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <SubSection index="C" title="Geometry / section setup" lead={`${geoms.length} geometr${geoms.length === 1 ? "y" : "ies"}`} testid="setup-section-c">
            <Toolbar>
                <span className="tc-eyebrow text-[#5a6669]">Project geometries</span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={isPro ? startDxfImport : undefined}
                        disabled={!isPro}
                        data-testid="setup-dxf-import-button"
                        title={isPro ? "Import DXF cross-section" : "Pro tier required — DXF import unlocks custom 2D pour cross-sections."}
                        className={`tc-mono text-[11.5px] px-3 py-1.5 border border-[#d8dcdd] inline-flex items-center gap-1.5 transition-colors ${isPro ? "text-[#0e1416] hover:border-[#01696f] hover:text-[#01696f]" : "text-[#8a9498] cursor-not-allowed opacity-60"}`}
                    >
                        Import DXF{!isPro && <span className="tc-mono text-[10px] text-[#5a6669] ml-1">· Pro</span>}
                    </button>
                    <Btn onClick={() => { setEditingId(null); setAdding(true); }} testid="setup-add-geometry"><Plus size={13} strokeWidth={1.5} />Add geometry</Btn>
                </div>
            </Toolbar>
            {geoms.length === 0 && !adding && <p className="text-[13px] text-[#5a6669] mt-4">No geometries yet. Pick a parametric shape and assign boundary conditions per segment.</p>}
            {geoms.length > 0 && (
                <ul className="divide-y divide-[#d8dcdd] border-t border-b border-[#d8dcdd]">
                    {geoms.map((g) => (
                        <li key={g.id} data-testid={`geom-row-${g.id}`} className="grid grid-cols-12 px-1 py-3 items-center gap-3">
                            <div className="col-span-2"><ShapePreview shape={g.shape_type} parameters={g.parameters} boundary_conditions={g.boundary_conditions} compact /></div>
                            <div className="col-span-4">
                                <div className="text-[13.5px] text-[#0e1416]">{g.name}</div>
                                <div className="tc-mono text-[10.5px] text-[#5a6669]">{g.shape_type}</div>
                            </div>
                            <div className="col-span-5 tc-mono text-[10.5px] text-[#5a6669]">
                                {Object.entries(g.parameters || {}).slice(0, 4).map(([k, v]) => <span key={k} className="mr-3">{k}={String(v)}</span>)}
                            </div>
                            <div className="col-span-1 flex justify-end gap-1">
                                <button onClick={() => { setAdding(false); setEditingId(g.id); }} className="p-1 text-[#5a6669] hover:text-[#01696f]" title="Edit" data-testid={`geom-edit-${g.id}`}><Edit2 size={13} strokeWidth={1.5} /></button>
                                <button onClick={() => onDelete(g.id)} className="p-1 text-[#5a6669] hover:text-[#a13434]" title="Delete"><Trash2 size={13} strokeWidth={1.5} /></button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            {adding && <GeometryForm pid={pid} meets={meets} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />}
            {editingId && <GeometryForm pid={pid} meets={meets} editing={geoms.find((g) => g.id === editingId)}
                                       onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); onReload(); }} />}

            {dxfFor && (
                <DxfImportModal
                    geometry={dxfFor}
                    projectId={pid}
                    onClose={() => setDxfFor(null)}
                    onCommitted={() => { setDxfFor(null); onReload(); }}
                />
            )}

            {/* BC legend */}
            <div className="mt-6 pt-5 border-t border-[#d8dcdd]">
                <span className="tc-eyebrow text-[#5a6669]">Boundary condition legend</span>
                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                    {Object.entries(BC_OPTIONS).map(([k, label]) => (
                        <li key={k} className="flex items-center gap-2 tc-mono text-[11px] text-[#5a6669]">
                            <span className="inline-block w-5 h-[2px]" style={{ background: BC_COLOR_OF(k) }} />
                            {label}
                        </li>
                    ))}
                </ul>
            </div>
        </SubSection>
    );
};

const GeometryForm = ({ pid, meets, editing, onCancel, onSaved }) => {
    const canAdvanced = meets("starter");
    const [shape, setShape] = useState(editing?.shape_type || "mass_pour");
    const [name, setName] = useState(editing?.name || "");
    const [params, setParams] = useState(editing?.parameters || SHAPE_DEFS[editing?.shape_type || "mass_pour"].defaults);
    const [bcs, setBcs] = useState(() => {
        if (!editing?.boundary_conditions) return {};
        const out = {};
        for (const bc of editing.boundary_conditions) { if (bc.segment_label) out[bc.segment_label] = bc.bc_type; }
        return out;
    });
    const [err, setErr] = useState(null);

    const segments = useMemo(() => SEGMENTS_FOR(shape), [shape]);
    useEffect(() => {
        // initialize BCs default to ambient
        const next = {};
        segments.forEach((seg) => { next[seg] = bcs[seg] || "ambient"; });
        setBcs(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shape]);

    const switchShape = (s) => {
        setShape(s);
        setParams(SHAPE_DEFS[s].defaults);
    };

    const save = async () => {
        setErr(null);
        if (!name.trim()) return setErr("Geometry name is required.");
        const boundary_conditions = segments.map((seg) => ({ segment_label: seg, bc_type: bcs[seg] || "ambient" }));
        try {
            if (editing) await projectApi.updateGeometry(editing.id, { name, shape_type: shape, parameters: params, boundary_conditions });
            else await projectApi.createGeometry(pid, { name, shape_type: shape, parameters: params, boundary_conditions });
            toast.success(editing ? "Geometry updated." : "Geometry saved.");
            onSaved();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div data-testid="geometry-form" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5 mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <EditCell label="Shape">
                        <Select value={shape} onChange={(e) => switchShape(e.target.value)} testid="geometry-form-shape" options={[
                            { value: "mass_pour", label: "Mass pour (rectangular)" },
                            { value: "phc_pile", label: "PHC pile" },
                            { value: "slab", label: canAdvanced ? "Slab" : "Slab · Starter+" },
                            { value: "wall", label: canAdvanced ? "Wall" : "Wall · Starter+" },
                        ]} />
                    </EditCell>
                    <div className="mt-4"><EditCell label="Name"><Input testid="geometry-form-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Block A · cross-section" /></EditCell></div>

                    <div className="mt-5 pt-4 border-t border-[#d8dcdd]">
                        <Lbl>Parameters</Lbl>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            {Object.keys(SHAPE_DEFS[shape].defaults).map((k) => (
                                <EditCell key={k} label={k.replace(/_/g, " ")}>
                                    {typeof SHAPE_DEFS[shape].defaults[k] === "boolean" ? (
                                        <Select value={String(params[k])} onChange={(e) => setParams({ ...params, [k]: e.target.value === "true" })} options={[{ value: "false", label: "no" }, { value: "true", label: "yes" }]} />
                                    ) : typeof SHAPE_DEFS[shape].defaults[k] === "string" ? (
                                        <Input value={params[k]} onChange={(e) => setParams({ ...params, [k]: e.target.value })} />
                                    ) : (
                                        <Input type="number" step="0.01" value={params[k]} onChange={(e) => setParams({ ...params, [k]: parseFloat(e.target.value) || 0 })} />
                                    )}
                                </EditCell>
                            ))}
                        </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-[#d8dcdd]">
                        <Lbl>Boundary conditions</Lbl>
                        <div className="grid grid-cols-1 gap-2 mt-2">
                            {segments.map((seg) => (
                                <div key={seg} className="grid grid-cols-12 items-center gap-2">
                                    <div className="col-span-4 tc-mono text-[11px] text-[#0e1416]">{seg}</div>
                                    <div className="col-span-8">
                                        <Select value={bcs[seg] || "ambient"} onChange={(e) => setBcs({ ...bcs, [seg]: e.target.value })} testid={`bc-${seg.toLowerCase().replace(/\s+/g, "-")}`} options={Object.entries(BC_OPTIONS).map(([k, label]) => ({ value: k, label }))} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="border border-[#d8dcdd] bg-[#ffffff] p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                        <span className="tc-eyebrow text-[#5a6669]">Preview</span>
                        <span className="tc-mono text-[10px] text-[#5a6669]">boundary-coloured</span>
                    </div>
                    <ShapePreview shape={shape} parameters={params} boundary_conditions={segments.map((s) => ({ segment_label: s, bc_type: bcs[s] || "ambient" }))} />
                </div>
            </div>

            {/* Custom locked card */}
            <div className="mt-5 border border-dashed border-[#d8dcdd] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 tc-mono text-[11px] text-[#5a6669]"><Lock size={12} strokeWidth={1.5} />Custom freeform geometries — coming in a later release.</div>
            </div>

            <ErrorLine msg={err} testid="geometry-form-error" />
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[#d8dcdd]">
                <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} testid="geometry-form-save">Save geometry</Btn>
            </div>
        </div>
    );
};

/* ========== D. Thermal scenario ========== */

const DEFAULT_AMBIENT = [
    { time_h: 0, T_C: 25 },
    { time_h: 12, T_C: 30 },
    { time_h: 24, T_C: 22 },
    { time_h: 48, T_C: 24 },
    { time_h: 72, T_C: 26 },
];

const SubSectionD = ({ pid, scenarios, onReload }) => {
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const onDelete = async (id) => {
        try { await projectApi.deleteScenario(id); onReload(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <SubSection index="D" title="Thermal scenario" lead={`${scenarios.length} scenario${scenarios.length === 1 ? "" : "s"}`} testid="setup-section-d">
            <Toolbar>
                <span className="tc-eyebrow text-[#5a6669]">Project scenarios</span>
                <Btn onClick={() => { setEditingId(null); setAdding(true); }} testid="setup-add-scenario"><Plus size={13} strokeWidth={1.5} />Add scenario</Btn>
            </Toolbar>
            {scenarios.length === 0 && !adding && <p className="text-[13px] text-[#5a6669] mt-4">No scenarios yet. Define ambient schedules, placement temperature, and optional pipe cooling for each pour-day context (e.g. summer vs winter).</p>}
            {scenarios.length > 0 && (
                <ul className="divide-y divide-[#d8dcdd] border-t border-b border-[#d8dcdd]">
                    {scenarios.map((s) => {
                        const ts = (s.ambient_schedule || []).map((p) => p.T_C);
                        const tmin = ts.length ? Math.min(...ts) : null;
                        const tmax = ts.length ? Math.max(...ts) : null;
                        return (
                            <li key={s.id} data-testid={`scenario-row-${s.id}`} className="grid grid-cols-12 px-1 py-3 items-center">
                                <div className="col-span-4 text-[13.5px] text-[#0e1416]">{s.name}</div>
                                <div className="col-span-7 tc-mono text-[11px] text-[#5a6669]">T_amb {tmin ?? "—"}–{tmax ?? "—"}°C · T_placement {s.placement_temp}°C · pipe cooling {s.pipe_cooling_enabled ? "on" : "off"}</div>
                                <div className="col-span-1 flex justify-end gap-1">
                                    <button onClick={() => { setAdding(false); setEditingId(s.id); }} className="p-1 text-[#5a6669] hover:text-[#01696f]" title="Edit" data-testid={`scenario-edit-${s.id}`}><Edit2 size={13} strokeWidth={1.5} /></button>
                                    <button onClick={() => onDelete(s.id)} className="p-1 text-[#5a6669] hover:text-[#a13434]" title="Delete"><Trash2 size={13} strokeWidth={1.5} /></button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {adding && <ScenarioForm pid={pid} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />}
            {editingId && <ScenarioForm pid={pid} editing={scenarios.find((s) => s.id === editingId)}
                                      onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); onReload(); }} />}
        </SubSection>
    );
};

const ScenarioForm = ({ pid, editing, onCancel, onSaved }) => {
    const [f, setF] = useState(() => editing ? {
        name: editing.name || "",
        placement_temp: editing.placement_temp ?? 28,
        wind_factor: editing.wind_factor ?? 1.0,
        solar_gain: editing.solar_gain ?? 0,
        pipe_cooling_enabled: editing.pipe_cooling_enabled || false,
        pipe_cooling: editing.pipe_cooling || { n_pipes: 0, diameter_mm: 0, flow_rate_lpm: 0, inlet_T_C: 20 },
        insulation_R: editing.insulation_R ?? "",
    } : {
        name: "", placement_temp: 28, wind_factor: 1.0, solar_gain: 0, pipe_cooling_enabled: false,
        pipe_cooling: { n_pipes: 0, diameter_mm: 0, flow_rate_lpm: 0, inlet_T_C: 20 },
        insulation_R: "",
    });
    const [ambient, setAmbient] = useState(() => editing?.ambient_schedule?.length ? [...editing.ambient_schedule] : [...DEFAULT_AMBIENT]);
    const [err, setErr] = useState(null);

    const addRow = () => setAmbient([...ambient, { time_h: (ambient[ambient.length - 1]?.time_h || 0) + 12, T_C: 25 }]);
    const removeRow = (i) => setAmbient(ambient.filter((_, idx) => idx !== i));
    const setRow = (i, patch) => setAmbient(ambient.map((p, idx) => idx === i ? { ...p, ...patch } : p));

    const save = async () => {
        setErr(null);
        if (!f.name.trim()) return setErr("Scenario name is required.");
        try {
            const body = {
                ...f,
                ambient_schedule: ambient,
                insulation_R: f.insulation_R === "" ? null : parseFloat(f.insulation_R) || null,
            };
            if (editing) await projectApi.updateScenario(editing.id, body);
            else await projectApi.createScenario(pid, body);
            toast.success(editing ? "Scenario updated." : "Scenario saved.");
            onSaved();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div data-testid="scenario-form" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5 mt-5">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <EditCell label="Name"><Input testid="scenario-form-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Summer pour · HK · midnight" /></EditCell>
                <EditCell label="Placement T (°C)"><Input type="number" step="0.5" value={f.placement_temp} onChange={(e) => setF({ ...f, placement_temp: parseFloat(e.target.value) || 0 })} /></EditCell>
                <EditCell label="Wind factor"><Input type="number" step="0.1" value={f.wind_factor} onChange={(e) => setF({ ...f, wind_factor: parseFloat(e.target.value) || 0 })} /></EditCell>
                <EditCell label="Solar gain (W/m²)"><Input type="number" step="10" value={f.solar_gain} onChange={(e) => setF({ ...f, solar_gain: parseFloat(e.target.value) || 0 })} /></EditCell>
            </div>

            <div className="mt-5 pt-4 border-t border-[#d8dcdd]">
                <div className="flex items-baseline justify-between mb-2">
                    <Lbl>Ambient schedule</Lbl>
                    <button type="button" onClick={addRow} className="tc-mono text-[11px] text-[#01696f] hover:text-[#015a60]">+ add row</button>
                </div>
                <div className="grid grid-cols-12 gap-3 tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase mt-1">
                    <div className="col-span-3">Time offset (h)</div>
                    <div className="col-span-3">T ambient (°C)</div>
                </div>
                {ambient.map((p, i) => (
                    <div key={i} className="grid grid-cols-12 gap-3 items-center py-1.5">
                        <div className="col-span-3"><Input type="number" step="0.5" value={p.time_h} onChange={(e) => setRow(i, { time_h: parseFloat(e.target.value) || 0 })} /></div>
                        <div className="col-span-3"><Input type="number" step="0.5" value={p.T_C} onChange={(e) => setRow(i, { T_C: parseFloat(e.target.value) || 0 })} /></div>
                        <div className="col-span-1"><button type="button" onClick={() => removeRow(i)} className="p-1 text-[#5a6669] hover:text-[#a13434]"><Trash2 size={12} strokeWidth={1.5} /></button></div>
                    </div>
                ))}
            </div>

            <div className="mt-5 pt-4 border-t border-[#d8dcdd]">
                <label className="flex items-center gap-2 cursor-pointer text-[12.5px] text-[#0e1416]">
                    <input type="checkbox" checked={f.pipe_cooling_enabled} onChange={(e) => setF({ ...f, pipe_cooling_enabled: e.target.checked })} data-testid="scenario-pipe-toggle" />
                    Pipe cooling enabled
                </label>
                {f.pipe_cooling_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-3">
                        <EditCell label="# pipes"><Input type="number" step="1" value={f.pipe_cooling.n_pipes} onChange={(e) => setF({ ...f, pipe_cooling: { ...f.pipe_cooling, n_pipes: parseFloat(e.target.value) || 0 } })} /></EditCell>
                        <EditCell label="Diameter (mm)"><Input type="number" step="1" value={f.pipe_cooling.diameter_mm} onChange={(e) => setF({ ...f, pipe_cooling: { ...f.pipe_cooling, diameter_mm: parseFloat(e.target.value) || 0 } })} /></EditCell>
                        <EditCell label="Flow (L/min)"><Input type="number" step="1" value={f.pipe_cooling.flow_rate_lpm} onChange={(e) => setF({ ...f, pipe_cooling: { ...f.pipe_cooling, flow_rate_lpm: parseFloat(e.target.value) || 0 } })} /></EditCell>
                        <EditCell label="Inlet T (°C)"><Input type="number" step="0.5" value={f.pipe_cooling.inlet_T_C} onChange={(e) => setF({ ...f, pipe_cooling: { ...f.pipe_cooling, inlet_T_C: parseFloat(e.target.value) || 0 } })} /></EditCell>
                    </div>
                )}
                <div className="mt-4"><EditCell label="Insulation R-value override (optional)"><Input type="number" step="0.1" value={f.insulation_R} onChange={(e) => setF({ ...f, insulation_R: e.target.value })} placeholder="leave blank to use geometry-level" /></EditCell></div>
            </div>

            <ErrorLine msg={err} testid="scenario-form-error" />
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[#d8dcdd]">
                <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} testid="scenario-form-save">Save scenario</Btn>
            </div>
        </div>
    );
};
